type Args = {
  /** 募集一覧のエントリーボタンのURL。自サイトの計測ページ（/tracking/...）への絶対URLが入る */
  entryUrl: string;
  /** 自サイト内のパスに解決できなかったときの戻り先（募集詳細ページ） */
  fallbackPath: string;
  /** 自サイトのオリジン（NEXT_PUBLIC_DOMAIN_URL） */
  siteOrigin: string | undefined;
};

// 相対パスの解決に使う実在しないホスト。同一オリジンに解決されたものだけが自サイト内のパスになる
const DUMMY_ORIGIN = 'https://internal.invalid';

// 保存先 gs_register_temp.in_flow_path の列長。超えると登録メール送信自体が失敗するため戻り先を諦める
const MAX_INFLOW_PATH_LENGTH = 2048;

/**
 * 登録モーダルから会員登録したあとにエントリーへ戻るための inflowPath を決める。
 * 登録完了ページの「元の画面に戻る」とログイン後の復帰先に使うため、自サイト内のパスに限定する。
 *
 * 文字列の前方一致では防ぎきれない。`/\evil.com` はブラウザがバックスラッシュを
 * スラッシュに正規化するため scheme-relative URL として外部に解決される。
 */
export const resolveEntryRegisterInflowPath = ({
  entryUrl,
  fallbackPath,
  siteOrigin,
}: Args): string => {
  // バックスラッシュはブラウザがスラッシュに正規化する。制御文字と空白は除去されてから解釈されうる
  const hasUnsafeChar = entryUrl
    .split('')
    .some((c) => c === '\\' || c.charCodeAt(0) <= 0x20);
  if (hasUnsafeChar) {
    return fallbackPath;
  }

  const base = entryUrl.startsWith('/') ? DUMMY_ORIGIN : siteOrigin;
  if (!base) {
    return fallbackPath;
  }

  let baseOrigin: string;
  let url: URL;
  try {
    baseOrigin = new URL(base).origin;
    url = new URL(entryUrl, baseOrigin);
  } catch {
    return fallbackPath;
  }

  if (url.origin !== baseOrigin) {
    return fallbackPath;
  }

  // オリジン比較だけでは `/..//` 始まりの値を通してしまう
  if (url.pathname.startsWith('//')) {
    return fallbackPath;
  }

  const path = `${url.pathname}${url.search}`;
  if (path.length > MAX_INFLOW_PATH_LENGTH) {
    return fallbackPath;
  }

  return path;
};
